<script lang='ts'>
  import '$lib/app.css'
  import { toast, Toaster } from 'svelte-sonner'

  import { describeError } from '$lib/errors'
  import { SubtitleManager } from '$lib/subtitles'

  const context = cast.framework.CastReceiverContext.getInstance()
  const playerManager = context.getPlayerManager()

  const subInstance = new SubtitleManager()

  context.addCustomMessageListener('urn:x-cast:com.url.cast', data => subInstance.onmessage(data))

  // Debug Logger
// const castDebugLogger = cast.debug.CastDebugLogger.getInstance();
// const LOG_TAG = 'MyAPP.LOG';

// Enable debug logger and show a 'DEBUG MODE' overlay at top left corner.
// castDebugLogger.setEnabled(true);

// Show debug overlay
// castDebugLogger.showDebugLogs(true);

// Set verbosity level for Core events.
// castDebugLogger.loggerLevelByEvents = {
//   'cast.framework.events.category.CORE': cast.framework.LoggerLevel.INFO,
//   'cast.framework.events.EventType.MEDIA_STATUS': cast.framework.LoggerLevel.DEBUG
// }

// // Set verbosity level for custom tags.
// castDebugLogger.loggerLevelByTags = {
//     LOG_TAG: cast.framework.LoggerLevel.DEBUG,
// };

  playerManager.addSupportedMediaCommands(
    cast.framework.messages.Command.STREAM_VOLUME |
    cast.framework.messages.Command.ALL_BASIC_MEDIA |
    cast.framework.messages.Command.STREAM_MUTE |
    cast.framework.messages.Command.STREAM_VOLUME |
    cast.framework.messages.Command.EDIT_TRACKS
  )

  // playerManager.setMessageInterceptor(cast.framework.messages.MessageType.USER_ACTION,
  //   (userActionRequestData) => {
  //     // Obtain the media information of the current content to associate the action to.
  //     const action = userActionRequestData.userActionContext
  //     subInstance.log('USER_ACTION ' + action)

  //     return userActionRequestData
  //   })

  playerManager.setMessageInterceptor(cast.framework.messages.MessageType.EDIT_TRACKS_INFO,
    (trackdata) => {
      try {
        const id = subInstance.cycleSubtitles()
        if (id !== -1) {
          trackdata.enableTextTracks = true
          delete trackdata.activeTrackIds
        } else {
          trackdata.activeTrackIds = []
          delete trackdata.enableTextTracks
        }
      } catch (e) {
        toast.error('Could not switch track', { description: describeError(e), duration: 15_000 })
      }

      return trackdata
    })

  // cast wants 2 letter language codes
  const languageCodes = {
    eng: 'en',
    jpn: 'jp',
    chi: 'zh',
    por: 'pt',
    spa: 'es',
    ger: 'de',
    pol: 'pl',
    cze: 'cs',
    dan: 'da',
    gre: 'el',
    fin: 'fi',
    fre: 'fr',
    hun: 'hu',
    ita: 'it',
    kor: 'ko',
    dut: 'nl',
    nor: 'no',
    rum: 'ro',
    rus: 'ru',
    slo: 'sk',
    swe: 'sv',
    ara: 'ar',
    idn: 'id',
    heb: 'he',
    vie: 'vi',
    tha: 'th',
    tur: 'tr',
    hin: 'hi',
    ben: 'bn',
    per: 'fa',
    mal: 'ml',
    '': 'en'
  } as const

  let audioLanguage = 'jp'

  playerManager.addEventListener(
    cast.framework.events.EventType.PLAYER_LOAD_COMPLETE, () => {
      try {
        const audioTracksManager = playerManager.getAudioTracksManager()
        audioTracksManager.setActiveByLanguage(audioLanguage)
      } catch (e) {}
    })

  // Intercept LOAD to append fake subtitle tracks
  playerManager.setMessageInterceptor(
    cast.framework.messages.MessageType.LOAD,
    async request => {
      try {
        request.media.tracks ??= [{
          trackId: 1000,
          type: cast.framework.messages.TrackType.TEXT,
          trackContentType: 'text/vtt',
          name: 'Fake Subtitle Track',
          language: 'xx',
          roles: ['subtitle'],
          subtype: 'subtitle'
        }]
        request.activeTrackIds = [1000]
        const customData = (request.customData ?? request.media.customData) as Record<string, string | undefined> | undefined
        // @ts-expect-error bad typedefs
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        if (customData?.audioLanguage) audioLanguage = languageCodes[customData.audioLanguage] || 'en'
        if (customData?.subtitleLanguage) subInstance.subtitleLanguage = customData.subtitleLanguage
        subInstance.setSrc()
      } catch (e) {
        toast.error('Could not load media', { description: describeError(e) })
      }
      return request
    })

  playerManager.addEventListener(
    cast.framework.events.EventType.ERROR,
    (event) => {
      const detail = event.error
      toast.error('Cast player error', { description: detail?.message ?? detail?.type ?? JSON.stringify(detail, null, 2) })
    }
  )

  const video = document.querySelector('cast-media-player')?.shadowRoot?.querySelector('video')
  if (video) {
    video.addEventListener('error', () => {
      const mediaError = video.error
      if (!mediaError) return
      const messages: Record<number, string> = {
        1: 'Media playback aborted',
        2: 'Network error loading media',
        3: 'Error decoding media',
        4: 'Media source not supported'
      }
      toast.error(messages[mediaError.code] ?? 'Unknown media error', { description: mediaError.message })
    })
  }

  // Optimizing for smart displays
  // const playerData = new cast.framework.ui.PlayerData()
  // const touchControls = cast.framework.ui.Controls.getInstance()
  // const playerDataBinder = new cast.framework.ui.PlayerDataBinder(playerData)

  // getBrowseItems().then(items => {
  //   const browseContent = new cast.framework.ui.BrowseContent(items, 'Up Next')
  //   browseContent.targetAspectRatio =
  //     cast.framework.ui.BrowseImageAspectRatio.LANDSCAPE_16_TO_9

  //   playerDataBinder.addEventListener(
  //     cast.framework.ui.PlayerDataEventType.MEDIA_CHANGED,
  //     (e) => {
  //       if (!e.value) return

  //       // Media browse
  //       touchControls.setBrowseContent(browseContent)

  //     // Clear default buttons and re-assign
  //     // touchControls.clearDefaultSlotAssignments()
  //     // touchControls.assignButton(
  //     //   cast.framework.ui.ControlsSlot.SLOT_PRIMARY_1,
  //     // cast.framework.ui.ControlsButton.SEEK_BACKWARD_30
  //       // )
  //     })
  // })

  // async function getBrowseItems () {
  //   const browseItems: BrowseItem[] = []
  //   const data = await makeRequest('GET', SAMPLE_URL)
  //   for (const key in data) {
  //     const item = new cast.framework.ui.BrowseItem(key, data[key].title, data[key].description, new cast.framework.messages.Image(data[key].poster))
  //     item.imageType = cast.framework.ui.BrowseImageType.MOVIE
  //     browseItems.push(item)
  //   }
  //   return browseItems
  // }

  context.start({
    customNamespaces: {
      'urn:x-cast:com.url.cast': cast.framework.system.MessageType.JSON
    }
  })

// on cast player mount, apply all styles to the shadow DOM
  // const element = document.querySelector('cast-media-player')
  // const stylesheet = new CSSStyleSheet()
  // for (const rule of document.styleSheets[0]?.cssRules ?? []) {
  //   stylesheet.insertRule(rule.cssText)
  // }
  // element?.shadowRoot?.adoptedStyleSheets.push(stylesheet)
</script>

<Toaster position='top-right' theme='dark' richColors />

<svelte:window
  on:unhandledrejection={(event) => {
    const reason = (event as unknown as PromiseRejectionEvent).reason
    toast.error('Unhandled promise rejection', { description: describeError(reason) })
  }}
  on:error={(event) => {
    const errorEvent = event as unknown as ErrorEvent
    const error = errorEvent.error ?? errorEvent.message
    toast.error('Uncaught error', { description: describeError(error) })
  }}
/>
<slot />
